import { formatPointsSimple } from "@vericus/cadmus-common";
import { QuestionRoundIcon } from "@vericus/cadmus-icons";
import { Divider, Pill, Text, Tooltip, vars } from "@vericus/cadmus-ui";

import clsx from "clsx";

import squigglyNoteIcon from "@/assets/illustrations/squiggly-note.svg";
import feedbackImg from "@/assets/illustrations/students-discussing.png";
import { ProvisionalFeedbackPill } from "@/features/feedback/components/provisional-feedback-pill";
import { FeedbackLevel } from "@/generated/graphql";

import * as styles from "./feedback-results.css";

interface Props {
  isProvisional: boolean;
  feedbackViewLevel: FeedbackLevel;
  finalGrade: number | null | undefined;
  maxGrade: number;
  moderatorModifier?: number | null;
  penaltyScore?: number | null;
}

export function FeedbackResults({
  isProvisional,
  maxGrade,
  feedbackViewLevel,
  finalGrade,
  moderatorModifier,
  penaltyScore,
}: Props) {
  const finalReleased = finalGrade !== undefined && finalGrade !== null;
  const showMarks = feedbackViewLevel !== FeedbackLevel.Feedback;
  const getScore = (score?: number | null) => {
    if (score === null || score === undefined) return "--";
    return formatPointsSimple(score);
  };

  const hasAdjustedPoints =
    Boolean(moderatorModifier && moderatorModifier !== 0) ||
    Boolean(penaltyScore && penaltyScore !== 0);

  const totalAdjustedPoints = formatPointsSimple(
    (moderatorModifier || 0) - (penaltyScore || 0),
    true
  );

  return (
    <div className={clsx(styles.feedbackOverviewResults, styles.feedbackLeft)}>
      <Text kind="displayThree" style={{ fontSize: "2rem" }}>
        {finalReleased ? (
          <>
            <span style={{ color: vars.colors.lilac500 }}>
              {isProvisional && "Provisional "}Feedback
            </span>{" "}
            has been released
          </>
        ) : (
          <>
            <span style={{ color: vars.colors.lilac500 }}>Draft Feedback</span>{" "}
            is available
          </>
        )}
      </Text>
      <div className={styles.feedbackResults}>
        <img src={feedbackImg} height={140} width={140} alt="" />
        {isProvisional && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ProvisionalFeedbackPill label={"Provisional Feedback"} />
          </div>
        )}
        {hasAdjustedPoints && (
          <div className={styles.adjustedPointsTooltipContent}>
            <Tooltip
              content={
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 8 }}
                >
                  {typeof moderatorModifier === "number" &&
                    moderatorModifier !== 0 && (
                      <div className={styles.adjustedPointsTooltipRow}>
                        <Text kind="systemMd" color="navy400">
                          Adjusted by Moderator
                        </Text>
                        <Text kind="systemMd" color="navy400">
                          {formatPointsSimple(moderatorModifier, true)}
                        </Text>
                      </div>
                    )}
                  {typeof penaltyScore === "number" && penaltyScore !== 0 && (
                    <div className={styles.adjustedPointsTooltipRow}>
                      <Text kind="systemMd" color="navy400">
                        Penalties applied
                      </Text>
                      <Text kind="systemMd" color="navy400">
                        {formatPointsSimple(0 - penaltyScore, true)}
                      </Text>
                    </div>
                  )}
                  <Divider />
                  <div className={styles.adjustedPointsTooltipRow}>
                    <Text kind="systemMd" color="navy400">
                      Total adjusted points
                    </Text>
                    <Text kind="systemMd" color="navy400">
                      {totalAdjustedPoints}
                    </Text>
                  </div>
                </div>
              }
              side="bottom"
              width="auto"
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <Text kind="systemMd" color="navy400">
                  Total Adjusted Points
                </Text>
                <QuestionRoundIcon label="" width={16} height={16} />
              </div>
            </Tooltip>
            <Pill color="#FAECE6">
              <Text kind="label" bold style={{ color: "#3B404F" }}>
                {totalAdjustedPoints}
              </Text>
            </Pill>
          </div>
        )}

        <div className={clsx(styles.gradeContent, styles.gradeResults)}>
          <img src={squigglyNoteIcon} width={39} height={32} alt="" />
          <div>
            <Text kind="headingFour">
              {finalReleased ? (
                <>
                  {showMarks ? getScore(finalGrade) : "xx.xx"}/{maxGrade}
                </>
              ) : (
                "-"
              )}
            </Text>
            <Text kind="label" bold>
              Grade
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}
